import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import PortalSnackbar from './Common/PortalSnackbar';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MusicNote as MusicNoteIcon,
} from '@mui/icons-material';
import api from '../utils/api';
import { 
  redirectToSpotifyLogin, 
  isSpotifyAuthenticated, 
  extractTrackId, 
  createPlaylist, 
  addTracksToPlaylist, 
  trackIdToUri,
  initializePlayer,
  playTrack,
  pausePlayback,
  resumePlayback,
  isPlayerInitialized,
  disconnectPlayer,
  shouldUseFallbackMode,
  isUserPremium,
  setUserPremiumStatus
} from '../utils/spotifyApi';
import useThemeStyles from '../theme/useThemeStyles';

function _TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`music-tabpanel-${index}`}
      aria-labelledby={`music-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function _a11yProps(index) {
  return {
    id: `music-tab-${index}`,
    'aria-controls': `music-tabpanel-${index}`,
  };
}

function MusicSelections({ embedded }) {
  const styles = useThemeStyles();
  const [musicSelections, setMusicSelections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [_saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingSelection, setEditingSelection] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [form, setForm] = useState({
    title: '',
    artist: '',
    category: '',
    notes: '',
    spotifyLink: '',
    youtubeLink: '',
  });
  
  // Spotify integration states
  const [spotifyConnected, setSpotifyConnected] = useState(false);
  const [spotifyConnectionInfo, setSpotifyConnectionInfo] = useState(null);
  const [spotifySearchQuery, setSpotifySearchQuery] = useState('');
  const [_spotifySearchResults, setSpotifySearchResults] = useState([]);
  const [_spotifySearching, setSpotifySearching] = useState(false);
  const [_showSpotifySearch, _setShowSpotifySearch] = useState(false);

  // Add new state variables for playlist creation
  const [_creatingPlaylist, setCreatingPlaylist] = useState(false);
  const [_playlistCategory, setPlaylistCategory] = useState(null);

  // Add new state variables for Spotify player
  const [playerInitialized, setPlayerInitialized] = useState(false);
  const [playerInitializing, setPlayerInitializing] = useState(false);
  const [currentlyPlayingTrack, setCurrentlyPlayingTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [_playerError, setPlayerError] = useState('');
  
  // Add state for playlists
  const [playlists, setPlaylists] = useState({
    ceremony: null,
    specialDances: null,
    reception: null,
    other: null
  });
  
  // Add state for snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const categories = [
    { value: 'processional', label: 'Processional' },
    { value: 'recessional', label: 'Recessional' },
    { value: 'firstDance', label: 'First Dance' },
    { value: 'fatherDaughterDance', label: 'Father-Daughter Dance' },
    { value: 'motherSonDance', label: 'Mother-Son Dance' },
    { value: 'dinner', label: 'Dinner Music' },
    { value: 'evening', label: 'Evening/Dancing' },
    { value: 'other', label: 'Other' },
  ];

  const _categoryToTabIndex = {
    'processional': 0,
    'recessional': 0,
    'firstDance': 1,
    'fatherDaughterDance': 1,
    'motherSonDance': 1,
    'dinner': 2,
    'evening': 2,
    'other': 3,
  };

  // Define musicCategories as the unique category labels
  const musicCategories = categories.map(cat => cat.label);

  useEffect(() => {
    fetchMusicSelections();
    checkSpotifyConnection();
    
    // Cleanup function to disconnect player when component unmounts
    return () => {
      disconnectPlayer();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, []);
  
  // Initialize Spotify player when connected
  useEffect(() => {
    if (spotifyConnected && !playerInitialized && !playerInitializing) {
      initializeSpotifyPlayer();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [spotifyConnected, playerInitialized, playerInitializing]);
  
  // Initialize the Spotify Web Playback SDK
  const initializeSpotifyPlayer = async () => {
    if (!spotifyConnected) return;
    
    try {
      setPlayerInitializing(true);
      setPlayerError('');
      
      // Check if dev tools are open
      try {
        if (typeof window !== 'undefined' && 
            window.__REACT_DEVTOOLS_GLOBAL_HOOK__ && 
            window.__REACT_DEVTOOLS_GLOBAL_HOOK__.renderers && 
            window.__REACT_DEVTOOLS_GLOBAL_HOOK__.renderers.size > 0) {
          setPlayerError('DevTools active: Songs will open in Spotify instead of playing in-app.');
          setPlayerInitialized(false);
          setPlayerInitializing(false);
          return;
        }
      } catch (e) {}
      
      // Check if we're already in fallback mode
      if (shouldUseFallbackMode()) {
        // Show different message for non-premium users
        if (!isUserPremium()) {
          setPlayerError('In-app music playback unavailable for non-Premium Spotify users. Songs will open in Spotify instead.');
        } else {
          setPlayerError('Using fallback mode: Songs will open in Spotify instead of playing in-app. This may be due to rate limiting.');
        }

        setPlayerInitialized(false);
        setPlayerInitializing(false);
        return;
      }
      
      await initializePlayer({
        onPlayerReady: (_deviceId) => {
          setPlayerInitialized(true);
          setPlayerInitializing(false);
          // If we got here, user must have premium
          setUserPremiumStatus(true);
        },
        onPlayerStateChanged: (state) => {
          if (!state) {
            setIsPlaying(false);
            return;
          }
          
          const trackId = state.track_window.current_track.id;
          setCurrentlyPlayingTrack(trackId);
          setIsPlaying(!state.paused);
        },
        onPlayerError: (error) => {
          // Check if we should use fallback mode
          if (shouldUseFallbackMode()) {
            // Show different message for non-premium users
            if (!isUserPremium() || error.includes('premium') || error === 'This functionality is restricted to premium users only') {
              setUserPremiumStatus(false);
              setPlayerError('In-app music playback unavailable for non-Premium Spotify users. Songs will open in Spotify instead.');
            } else {
              setPlayerError('Using fallback mode: Songs will open in Spotify instead of playing in-app. This may be due to rate limiting.');
            }
          } else if (error.includes('premium') || error === 'This functionality is restricted to premium users only') {
            setUserPremiumStatus(false);
            setPlayerError('In-app music playback unavailable for non-Premium Spotify users. Songs will open in Spotify instead.');
          } else {
            setPlayerError(`Spotify player error: ${error}`);
          }

          setPlayerInitialized(false);
          setPlayerInitializing(false);
        }
      });
      
      // Double-check fallback mode after initialization
      if (shouldUseFallbackMode()) {
        // Show different message for non-premium users
        if (!isUserPremium()) {
          setPlayerError('In-app music playback unavailable for non-Premium Spotify users. Songs will open in Spotify instead.');
        } else {
          setPlayerError('Using fallback mode: Songs will open in Spotify instead of playing in-app. This may be due to rate limiting.');
        }
        
        setPlayerInitialized(false);
      }
    } catch (error) {
      setPlayerError('Failed to initialize Spotify player. Songs will open in Spotify instead.');
      setPlayerInitialized(false);
      setPlayerInitializing(false);
    }
  };
  
  // Fetch existing playlists when connected
  useEffect(() => {
    if (spotifyConnected && spotifyConnectionInfo) {
      fetchExistingPlaylists();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [spotifyConnected, spotifyConnectionInfo]);
  
  // Fetch existing wedding playlists
  const fetchExistingPlaylists = async () => {
    try {
      const response = await api.get('/api/spotify/playlists');
      
      // Look for wedding playlists
      const weddingPlaylists = response.data.filter(playlist => 
        playlist.name.startsWith('Wedding -')
      );
      
      // Update playlists state
      const newPlaylists = { ...playlists };
      
      weddingPlaylists.forEach(playlist => {
        if (playlist.name.includes('Ceremony')) {
          newPlaylists.ceremony = playlist;
        } else if (playlist.name.includes('Special Dances')) {
          newPlaylists.specialDances = playlist;
        } else if (playlist.name.includes('Reception')) {
          newPlaylists.reception = playlist;
        } else if (playlist.name.includes('Other')) {
          newPlaylists.other = playlist;
        }
      });
      
      setPlaylists(newPlaylists);
    } catch (error) {}
  };

  const fetchMusicSelections = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/music-selections');
      setMusicSelections(response.data || []);
    } catch (error) {
      setError('Unable to load your music selections');
    } finally {
      setLoading(false);
    }
  };

  const checkSpotifyConnection = async () => {
    try {
      // Check if user is authenticated with Spotify using local storage tokens
      const isConnected = isSpotifyAuthenticated();
      setSpotifyConnected(isConnected);
      
      if (isConnected) {
        try {
          // Get connection info from the backend
          const response = await api.get('/api/spotify/connection');
          setSpotifyConnectionInfo(response.data);
          
          // If backend says not connected but we have local tokens, clear them
          if (!response.data.isConnected) {
            localStorage.removeItem('spotify_access_token');
            localStorage.removeItem('spotify_refresh_token');
            localStorage.removeItem('spotify_token_expiry');
            setSpotifyConnected(false);
          }
        } catch (backendError) {}
      } else {
        // Clear any connection info if we're not connected
        setSpotifyConnectionInfo(null);
      }
    } catch (error) {
      setSpotifyConnected(false);
      setSpotifyConnectionInfo(null);
    }
    
    // After checking connection, initialize player if connected
    if (spotifyConnected && !playerInitialized && !playerInitializing) {
      initializeSpotifyPlayer();
    }
  };

  const _handleConnectSpotify = () => {
    redirectToSpotifyLogin();
  };

  const _handleDisconnectSpotify = async () => {
    try {
      await api.delete('/api/spotify/connection');
      setSpotifyConnected(false);
      setSpotifyConnectionInfo(null);
      setSuccess('Spotify disconnected successfully');
    } catch (error) {
      setError('Failed to disconnect from Spotify');
    }
  };

  const _handleSpotifySearch = async () => {
    if (!spotifySearchQuery.trim()) return;
    
    try {
      setSpotifySearching(true);
      setError('');
      
      // First check if the Spotify connection is still valid
      const isConnected = isSpotifyAuthenticated();
      if (!isConnected) {
        setError('Your Spotify session has expired. Please reconnect to Spotify.');
        setSpotifyConnected(false);
        setSpotifyConnectionInfo(null);
        setSpotifySearching(false);
        return;
      }
      
      const response = await api.get('/api/spotify/search', {
        params: { q: spotifySearchQuery }
      });
      
      setSpotifySearchResults(response.data);
    } catch (error) {
      // Check if this is an authentication error
      if (error.response?.status === 401) {
        // Clear Spotify tokens from localStorage
        localStorage.removeItem('spotify_access_token');
        localStorage.removeItem('spotify_refresh_token');
        localStorage.removeItem('spotify_token_expiry');
        
        setSpotifyConnected(false);
        setSpotifyConnectionInfo(null);
        setError('Your Spotify connection has expired. Please reconnect to continue searching.');
      } else {
        setError('Failed to search Spotify. Please try again.');
      }
    } finally {
      setSpotifySearching(false);
    }
  };

  // Function to clear the search query and results
  const _handleClearSearch = () => {
    setSpotifySearchQuery('');
    setSpotifySearchResults([]);
  };

  const _handleAddFromSpotify = (track) => {
    // Prepare form data from Spotify track
    const newForm = {
      title: track.name,
      artist: track.artists.map(artist => artist.name).join(', '),
      category: categories[tabValue === 3 ? 7 : (tabValue === 2 ? 5 : (tabValue === 1 ? 2 : 0))].value,
      notes: '',
      spotifyLink: track.external_urls.spotify,
      youtubeLink: '',
    };
    
    setForm(newForm);
    setOpenDialog(true);
  };

  const _handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenDialog = (selection = null) => {
    if (selection) {
      setEditingSelection(selection);
      setForm({
        title: selection.title,
        artist: selection.artist,
        category: selection.category,
        notes: selection.notes || '',
        spotifyLink: selection.spotifyLink || '',
        youtubeLink: selection.youtubeLink || '',
      });
    } else {
      setEditingSelection(null);
      setForm({
        title: '',
        artist: '',
        category: categories[tabValue === 3 ? 7 : (tabValue === 2 ? 5 : (tabValue === 1 ? 2 : 0))].value,
        notes: '',
        spotifyLink: '',
        youtubeLink: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!form.title.trim()) {
      setError('Song title is required');
      return false;
    }
    if (!form.artist.trim()) {
      setError('Artist name is required');
      return false;
    }
    if (!form.category) {
      setError('Category is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Prepare form data with proper validation
      const formData = {
        title: form.title.trim(),
        artist: form.artist.trim(),
        category: form.category,
        notes: form.notes.trim(),
        spotifyLink: form.spotifyLink.trim(),
        youtubeLink: form.youtubeLink.trim()
      };

      let savedSelection;

      if (editingSelection && editingSelection._id) {
        // Update existing selection
        const response = await api.put(`/api/music-selections/${editingSelection._id}`, formData);

        // Update local state
        setMusicSelections(prev => 
          prev.map(item => item._id === editingSelection._id ? response.data : item)
        );

        savedSelection = response.data;
        setSuccess('Music selection updated successfully');
      } else {
        // Create new selection
        const response = await api.post('/api/music-selections', formData);

        // Update local state
        setMusicSelections(prev => [...prev, response.data]);

        savedSelection = response.data;
        setSuccess('Music selection added successfully');
      }

      // If connected to Spotify and the song has a Spotify link, update the appropriate playlist
      if (spotifyConnected && spotifyConnectionInfo && savedSelection.spotifyLink) {
        updatePlaylistWithSong(savedSelection);
      }

      handleCloseDialog();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save music selection');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await api.delete(`/api/music-selections/${id}`);
      
      // Update local state
      setMusicSelections(prev => prev.filter(item => item._id !== id));
      
      setSuccess('Music selection deleted successfully');
    } catch (error) {
      setError('Failed to delete music selection');
    } finally {
      setLoading(false);
    }
  };

  // Filter music by category based on current tab
  const _getFilteredMusic = () => {
    if (tabValue === 0) {
      return musicSelections.filter(music => 
        music.category === 'processional' || music.category === 'recessional'
      );
    } else if (tabValue === 1) {
      return musicSelections.filter(music => 
        music.category === 'firstDance' || 
        music.category === 'fatherDaughterDance' || 
        music.category === 'motherSonDance'
      );
    } else if (tabValue === 2) {
      return musicSelections.filter(music => 
        music.category === 'dinner' || music.category === 'evening'
      );
    } else {
      return musicSelections.filter(music => music.category === 'other');
    }
  };

  const _getCategoryLabel = (categoryValue) => {
    const category = categories.find(cat => cat.value === categoryValue);
    return category ? category.label : categoryValue;
  };

  // Function to create a Spotify playlist from a category
  const _handleCreatePlaylist = async (categoryName) => {
    if (!spotifyConnected || !spotifyConnectionInfo) {
      setError('Please connect to Spotify first to create playlists');
      return;
    }
    
    try {
      setCreatingPlaylist(true);
      setPlaylistCategory(categoryName);
      setError('');
      
      // Get songs for this category
      let songsToAdd = [];
      
      if (categoryName === 'Ceremony') {
        songsToAdd = musicSelections.filter(music => 
          music.category === 'processional' || music.category === 'recessional'
        );
      } else if (categoryName === 'Special Dances') {
        songsToAdd = musicSelections.filter(music => 
          music.category === 'firstDance' || 
          music.category === 'fatherDaughterDance' || 
          music.category === 'motherSonDance'
        );
      } else if (categoryName === 'Reception') {
        songsToAdd = musicSelections.filter(music => 
          music.category === 'dinner' || music.category === 'evening'
        );
      } else if (categoryName === 'Other') {
        songsToAdd = musicSelections.filter(music => 
          music.category === 'other'
        );
      } else {
        // If a specific category is provided
        songsToAdd = musicSelections.filter(music => 
          music.category === categoryName.toLowerCase().replace(/\s+/g, '')
        );
      }
      
      // Filter songs that have Spotify links
      const songsWithSpotifyLinks = songsToAdd.filter(song => song.spotifyLink);
      
      if (songsWithSpotifyLinks.length === 0) {
        setError(`No songs with Spotify links found in the ${categoryName} category`);
        setCreatingPlaylist(false);
        setPlaylistCategory(null);
        return;
      }
      
      // Create a new playlist
      const playlistName = `Wedding - ${categoryName}`;
      const playlistDescription = `Songs for the ${categoryName} part of our wedding`;
      
      const newPlaylist = await createPlaylist(
        spotifyConnectionInfo.spotifyUserId,
        playlistName,
        playlistDescription,
        false // not public
      );
      
      // Add tracks to the playlist
      const trackUris = songsWithSpotifyLinks
        .map(song => extractTrackId(song.spotifyLink))
        .filter(id => id) // Remove any null values
        .map(id => trackIdToUri(id));
      
      if (trackUris.length > 0) {
        await addTracksToPlaylist(newPlaylist.id, trackUris);
        
        setSuccess(`Successfully created "${playlistName}" playlist in Spotify with ${trackUris.length} songs`);
      } else {
        setError('Could not add any songs to the playlist. Please check your Spotify links.');
      }
    } catch (error) {
      setError('Failed to create Spotify playlist. Please try again.');
    } finally {
      setCreatingPlaylist(false);
      setPlaylistCategory(null);
    }
  };

  // Function to update the appropriate playlist with a song
  const updatePlaylistWithSong = async (song) => {
    if (!song.spotifyLink) return;
    
    try {
      const trackId = extractTrackId(song.spotifyLink);
      if (!trackId) return;
      
      const trackUri = trackIdToUri(trackId);
      let playlistId = null;
      let playlistName = '';
      let shouldCreatePlaylist = false;
      
      // Determine which playlist to update based on the song category
      if (song.category === 'processional' || song.category === 'recessional') {
        if (playlists.ceremony) {
          playlistId = playlists.ceremony.id;
          playlistName = playlists.ceremony.name;
        } else {
          shouldCreatePlaylist = true;
          playlistName = 'Wedding - Ceremony';
        }
      } else if (song.category === 'firstDance' || song.category === 'fatherDaughterDance' || song.category === 'motherSonDance') {
        if (playlists.specialDances) {
          playlistId = playlists.specialDances.id;
          playlistName = playlists.specialDances.name;
        } else {
          shouldCreatePlaylist = true;
          playlistName = 'Wedding - Special Dances';
        }
      } else if (song.category === 'dinner' || song.category === 'evening') {
        if (playlists.reception) {
          playlistId = playlists.reception.id;
          playlistName = playlists.reception.name;
        } else {
          shouldCreatePlaylist = true;
          playlistName = 'Wedding - Reception';
        }
      } else if (song.category === 'other') {
        if (playlists.other) {
          playlistId = playlists.other.id;
          playlistName = playlists.other.name;
        } else {
          shouldCreatePlaylist = true;
          playlistName = 'Wedding - Other';
        }
      }
      
      // Create playlist if needed
      if (shouldCreatePlaylist) {
        const categoryName = playlistName.replace('Wedding - ', '');
        const playlistDescription = `Songs for the ${categoryName} part of our wedding`;
        
        const newPlaylist = await createPlaylist(
          spotifyConnectionInfo.spotifyUserId,
          playlistName,
          playlistDescription,
          false // not public
        );
        
        playlistId = newPlaylist.id;
        
        // Update playlists state
        const newPlaylists = { ...playlists };
        if (categoryName === 'Ceremony') {
          newPlaylists.ceremony = newPlaylist;
        } else if (categoryName === 'Special Dances') {
          newPlaylists.specialDances = newPlaylist;
        } else if (categoryName === 'Reception') {
          newPlaylists.reception = newPlaylist;
        } else if (categoryName === 'Other') {
          newPlaylists.other = newPlaylist;
        }
        
        setPlaylists(newPlaylists);
      }
      
      // Add track to playlist
      if (playlistId) {
        await addTracksToPlaylist(playlistId, [trackUri]);
        
        // Show success message
        setSnackbar({
          open: true,
          message: `Added "${song.title}" to "${playlistName}" playlist`,
          severity: 'success'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to update playlist with song',
        severity: 'error'
      });
    }
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Handle playing a track
  const handlePlayTrack = async (trackId, spotifyLink) => {
    if (!spotifyConnected) {
      setError('Please connect to Spotify first to play tracks');
      return;
    }
    
    try {
      // Check if we're in fallback mode
      if (shouldUseFallbackMode()) {
        // Just open the track in Spotify
        window.open(spotifyLink, '_blank');
        
        // Update UI state for consistency
        setCurrentlyPlayingTrack(trackId);
        setIsPlaying(true);
        
        // Dispatch event to notify MusicTable components
        window.dispatchEvent(new CustomEvent('spotify-playing-state-changed', {
          detail: { trackId, isPlaying: true }
        }));
        
        return;
      }
      
      // If player is not initialized, try to initialize it
      if (!playerInitialized) {
        if (!playerInitializing) {
          await initializeSpotifyPlayer();
          
          // If initialization put us in fallback mode, just open in Spotify
          if (shouldUseFallbackMode()) {
            window.open(spotifyLink, '_blank');
            return;
          }
        } else {
          setError('Spotify player is initializing, please wait...');
          return;
        }
      }
      
      // Check if player is ready
      if (!isPlayerInitialized()) {
        // If not ready, use fallback
        window.open(spotifyLink, '_blank');
        return;
      }
      
      // If this track is already playing, pause it
      if (currentlyPlayingTrack === trackId && isPlaying) {
        await pausePlayback();
        setIsPlaying(false);
        return;
      }
      
      // If this track is paused, resume it
      if (currentlyPlayingTrack === trackId && !isPlaying) {
        await resumePlayback();
        setIsPlaying(true);
        return;
      }
      
      // Otherwise, play the new track
      const uri = trackIdToUri(trackId);
      await playTrack(uri);
      setCurrentlyPlayingTrack(trackId);
      setIsPlaying(true);
      
      // Dispatch event to notify MusicTable components
      window.dispatchEvent(new CustomEvent('spotify-playing-state-changed', {
        detail: { trackId, isPlaying: true }
      }));
    } catch (error) {
      // Check if this is a premium account error
      if (error.response?.status === 403) {
        setUserPremiumStatus(false);
        setError('In-app music playback unavailable for non-Premium Spotify users. Songs will open in Spotify instead.');
        // Set fallback mode
        localStorage.setItem('spotify_player_fallback', 'true');
        // Try fallback
        window.open(spotifyLink, '_blank');
      } else if (error.response?.status === 429) {
        setError('Spotify API rate limit reached. Songs will open in Spotify instead.');
        // Set fallback mode
        localStorage.setItem('spotify_player_fallback', 'true');
        // Try fallback
        window.open(spotifyLink, '_blank');
      } else {
        setError('Failed to play track. Please try again or open in Spotify.');
        // Try fallback
        window.open(spotifyLink, '_blank');
      }
    }
  };
  
  // Make handlePlayTrack available to MusicTable components
  useEffect(() => {
    if (!window.MusicSelectionsComponent) {
      window.MusicSelectionsComponent = {};
    }
    window.MusicSelectionsComponent.handlePlayTrack = handlePlayTrack;
    
    return () => {
      // Clean up
      if (window.MusicSelectionsComponent) {
        window.MusicSelectionsComponent.handlePlayTrack = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [spotifyConnected, playerInitialized, playerInitializing, currentlyPlayingTrack, isPlaying]);
  
  // Update player state when it changes
  useEffect(() => {
    if (currentlyPlayingTrack) {
      window.dispatchEvent(new CustomEvent('spotify-playing-state-changed', {
        detail: { trackId: currentlyPlayingTrack, isPlaying }
      }));
    }
  }, [currentlyPlayingTrack, isPlaying]);

  if (loading && musicSelections.length === 0) {
    return (
      <Box sx={styles.patterns.loadingState}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={embedded ? { p: 0 } : styles.container.page}>
      <Box sx={{
        ...styles.patterns.pageHeader,
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 2, sm: 0 },
        mb: embedded ? 1.5 : undefined,
      }}>
        <Box sx={{ flex: 1, px: embedded ? 2 : 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Music
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose your ceremony and reception music
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          size="medium"
          sx={{ alignSelf: { xs: 'stretch', sm: 'inherit' }, minWidth: { xs: 'auto', sm: undefined } }}
        >
          Add Song
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {musicSelections.length === 0 ? (
        <Card sx={styles.card.default}>
          <CardContent>
            <Box sx={styles.patterns.emptyState}>
              <MusicNoteIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No music selections yet
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Start adding songs for different moments of your wedding
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenDialog}
              >
                Add Your First Song
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {musicCategories.map(categoryLabel => {
            // Find the category object that matches this label
            const categoryObj = categories.find(cat => cat.label === categoryLabel);
            if (!categoryObj) return null;
            
            // Filter by category value, not label
            const categorySelections = musicSelections.filter(s => s.category === categoryObj.value);
            if (categorySelections.length === 0) return null;
            
            return (
              <Grid item xs={12} md={6} key={categoryLabel}>
                <Card sx={styles.card.default}>
                  <CardContent>
                    <Box sx={{ ...styles.layout.flexRow, mb: 2, gap: 1 }}>
                      <MusicNoteIcon color="primary" />
                      <Typography variant="h6">
                        {categoryLabel}
                      </Typography>
                    </Box>
                    
                    <List>
                      {categorySelections.map((selection) => (
                        <ListItem
                          key={selection._id}
                          sx={{
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                            mb: 1,
                          }}
                        >
                          <ListItemText
                            primary={selection.title}
                            secondary={
                              <>
                                <Typography component="span" variant="body2">
                                  {selection.artist}
                                </Typography>
                                {selection.notes && (
                                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                    {selection.notes}
                                  </Typography>
                                )}
                              </>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton size="small" onClick={() => handleOpenDialog(selection)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDelete(selection._id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSelection ? 'Edit Music Selection' : 'Add Music Selection'}
        </DialogTitle>
        <DialogContent>
          <Box sx={styles.patterns.formContainer} mt={2}>
            <FormControl fullWidth required>
              <InputLabel id="category-select-label">Category</InputLabel>
              <Select
                labelId="category-select-label"
                label="Category"
                value={form.category}
                onChange={(e) => handleChange({ target: { name: 'category', value: e.target.value } })}
              >
                {categories.map(cat => (
                  <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              name="title"
              label="Song Title"
              value={form.title}
              onChange={handleChange}
              required
            />
            
            <TextField
              fullWidth
              name="artist"
              label="Artist"
              value={form.artist}
              onChange={handleChange}
              required
            />
            
            <TextField
              fullWidth
              name="notes"
              label="Notes"
              value={form.notes}
              onChange={handleChange}
              multiline
              rows={2}
              placeholder="Special version, timing notes, etc."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!form.category || !form.title || !form.artist}
          >
            {editingSelection ? 'Update' : 'Add'} Song
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for playlist updates */}
      <PortalSnackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </PortalSnackbar>
    </Box>
  );
}

export default MusicSelections; 